import { reportDate } from '@/features/reports/lib/report-zone'

/**
 * The Buchhaltung report's period model (CHG-2026-10-accounting-close-periods, D1 and D7).
 *
 * Every period runs from one Tagesabschluss to another: a **Tag** is one close, a **Monat** every
 * close made in a calendar month, a **Zeitraum** the closes after one up to another. The server
 * owns the window rule; the page only names which closes it means, by their `closeRef` — the cloud
 * id of a close's latest Beleg — or by the month.
 *
 * Period and Ansicht live in the URL, so the back button, a shared link and clicking a close in a
 * list all come for free. Months are Vienna calendar months, like every reporting date in this app.
 */
export type AccountingMode = 'tag' | 'monat' | 'zeitraum'

export type Ansicht = 'uebersicht' | 'clemens'

export const ANSICHTEN: ReadonlyArray<Ansicht> = ['uebersicht', 'clemens']

/** What the URL may carry; anything malformed is dropped by {@link parseAccountingSearch}. */
export interface AccountingSearch {
  close?: string
  month?: string
  after?: string
  until?: string
  ansicht?: Ansicht
}

/** The period the URL selects — exactly one of the three. */
export type AccountingSelection =
  | { mode: 'tag'; close: string }
  | { mode: 'monat'; month: string }
  | { mode: 'zeitraum'; after: string; until: string }

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/
const REF = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The route's `validateSearch`: keeps what is well-formed and nothing else, so a mistyped link
 * falls back to the current month instead of asking the server a question nobody asked.
 */
export function parseAccountingSearch(
  search: Record<string, unknown>,
): AccountingSearch {
  const ref = (v: unknown) =>
    typeof v === 'string' && REF.test(v) ? v : undefined
  return {
    close: ref(search.close),
    month:
      typeof search.month === 'string' && MONTH.test(search.month)
        ? search.month
        : undefined,
    after: ref(search.after),
    until: ref(search.until),
    ansicht:
      search.ansicht === 'uebersicht' || search.ansicht === 'clemens'
        ? search.ansicht
        : undefined,
  }
}

/**
 * The selection the URL makes. A link with more than one selector is read in a fixed order —
 * Tag, then Zeitraum, then Monat — and one without any is the current month (D7).
 */
export function selectionOf(
  search: AccountingSearch,
  now: Date,
): AccountingSelection {
  if (search.close) return { mode: 'tag', close: search.close }
  if (search.after && search.until) {
    return { mode: 'zeitraum', after: search.after, until: search.until }
  }
  return { mode: 'monat', month: search.month ?? currentMonth(now) }
}

/** The search that selects {@code selection}, carrying the Ansicht along. */
export function searchOf(
  selection: AccountingSelection,
  ansicht: Ansicht | undefined,
): AccountingSearch {
  const base =
    selection.mode === 'tag'
      ? { close: selection.close }
      : selection.mode === 'zeitraum'
        ? { after: selection.after, until: selection.until }
        : { month: selection.month }
  return ansicht ? { ...base, ansicht } : base
}

/** `YYYY-MM` of today in Vienna. */
export function currentMonth(now: Date): string {
  return reportDate(now).slice(0, 7)
}

/** `YYYY-MM` shifted by whole months. */
export function shiftMonth(month: string, by: number): string {
  const [year, mon] = month.split('-').map(Number)
  const shifted = new Date(Date.UTC(year, mon - 1 + by, 1))
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`
}

/** `YYYY-MM` of an instant, in Vienna — the month a close belongs to. */
export function monthOf(iso: string): string {
  return reportDate(new Date(iso)).slice(0, 7)
}

/**
 * The query string for the period endpoint and its exports, or null when there is nothing to ask
 * yet (no Standort). `restaurantId` is required and single: a filing is per Standort.
 */
export function buildPeriodParams(
  selection: AccountingSelection,
  restaurantId: number | null,
): string | null {
  if (restaurantId == null) return null
  const params = new URLSearchParams({ restaurantId: String(restaurantId) })
  if (selection.mode === 'tag') params.set('close', selection.close)
  if (selection.mode === 'monat') params.set('month', selection.month)
  if (selection.mode === 'zeitraum') {
    params.set('after', selection.after)
    params.set('until', selection.until)
  }
  return params.toString()
}
